import { captureException } from '@canvas/sentry-core';

import { getImageDimensions } from './get-image-dimensions.ts';
import { getRawImageData } from './get-raw-image-data.ts';
import { loadImageFromFile } from './load-image.ts';
import type { MediaMetadata } from './media-metadata-types.ts';
import { sha256Hex } from './sha256.ts';

export interface ImageMetadata {
  /**
   * `true` if the image contains transparent pixels, `false` otherwise.
   */
  isTransparent: boolean;
  /**
   * A SHA-256 hash of the image data.
   *
   * Optional: if WebCrypto is unavailable we still return sizing + transparency information.
   */
  hash?: string;
  /**
   * The native size of the image in pixels.
   */
  nativeSize: { x: number; y: number };
}

/**
 * Returns metadata about an image, including whether it contains transparent pixels and
 * a hash of the image data.
 *
 * If there are any issues in determining image metadata, such as `getContext('2d')` not
 * being able to create a context, `null` will be returned.
 *
 * Feel free to reconsider and refactor the above decision if you are using
 * `getImageMetadata()` in a new code location.
 *
 * @param image HTMLImageElement
 * @returns The image metadata, or `null` if the image metadata could not be determined.
 */
export async function getImageMetadata(
  image: HTMLImageElement | HTMLCanvasElement,
): Promise<ImageMetadata | null> {
  try {
    const data = getRawImageData(image);

    // Check if the image contains transparent pixels
    let isTransparent = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        isTransparent = true;
        break;
      }
    }

    const hash = await sha256Hex(data.buffer);

    const nativeSize =
      'naturalWidth' in image && 'naturalHeight' in image
        ? { x: image.naturalWidth, y: image.naturalHeight }
        : { x: image.width, y: image.height };
    return {
      isTransparent,
      hash: hash ?? undefined,
      nativeSize,
    };
  } catch (e) {
    console.error('Failed to get image metadata', e);
    captureException(e, {
      tags: {
        captureLocation: 'getImageMetadata',
      },
    });

    return null;
  }
}

export async function getImageMetadataFromBlob(input: {
  blob: Blob;
  mimeType: string;
}): Promise<MediaMetadata | null> {
  try {
    const image = await loadImageFromFile(input.blob);

    const dimensions = getImageDimensions(image);
    const size = { x: dimensions.width, y: dimensions.height };

    const nativeWidth = dimensions.nativeWidth || dimensions.width;
    const nativeHeight = dimensions.nativeHeight || dimensions.height;
    const nativeSize = { x: nativeWidth, y: nativeHeight };

    const imageMetadata = await getImageMetadata(image);

    return {
      mimeType: input.mimeType,
      nativeSize,
      size,
      isTransparent: imageMetadata?.isTransparent ?? false,
      hash: imageMetadata?.hash,
    };
  } catch {
    return null;
  }
}
